import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, Loader, Camera, X, Image } from "lucide-react";
import { groundOpsApi } from "@/api";

const INCIDENT_TYPES = [
  "Aircraft Damage", "Baggage Issue", "Safety Hazard", "Staff Shortage",
  "Equipment Failure", "Passenger Boarding Issue", "Vendor Non-Performance",
  "Spill / Contamination", "FOD (Foreign Object Debris)", "Injury / Near Miss", "Other",
];
const SEVERITIES = [
  { value: "critical", label: "Critical — Ops impact", color: "bg-red-500/10 border-red-500/30 text-red-400" },
  { value: "high",     label: "High — Urgent",          color: "bg-orange-500/10 border-orange-500/30 text-orange-400" },
  { value: "medium",   label: "Medium — Monitor",       color: "bg-amber-500/10 border-amber-500/30 text-amber-400" },
  { value: "low",      label: "Low — For record",       color: "bg-slate-700/30 border-slate-600/30 text-slate-400" },
];

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = e => resolve(e.target.result);   // returns "data:image/...;base64,..."
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function RampIncident({ user }) {
  const navigate   = useNavigate();
  const fileRef    = useRef(null);

  const [type, setType]         = useState("");
  const [severity, setSeverity] = useState("medium");
  const [desc, setDesc]         = useState("");
  const [photos, setPhotos]     = useState([]);   // array of base64 data-URLs
  const [photoLoading, setPhotoLoading] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [done, setDone]         = useState(false);

  async function handleFileChange(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPhotoLoading(true);
    try {
      const b64s = await Promise.all(files.map(readFileAsBase64));
      setPhotos(prev => [...prev, ...b64s]);
    } catch (err) {
      console.error("Photo read error", err);
    } finally {
      setPhotoLoading(false);
      // Reset input so same file can be picked again
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function removePhoto(idx) {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  }

  async function submit() {
    if (!type || !desc) return;
    setSaving(true);
    try {
      await groundOpsApi.createIncident({
        station:       user.station,
        incident_type: type,
        description:   desc,
        reported_by:   user.name,
        severity,
        photo_urls:    photos,
      });
      setDone(true);
    } finally { setSaving(false); }
  }

  function reset() {
    setDone(false); setType(""); setDesc(""); setSeverity("medium"); setPhotos([]);
  }

  return (
    <div className="p-4 max-w-sm mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 text-sm mb-4">
        <ArrowLeft size={16} /> Back
      </button>

      {done ? (
        <div className="text-center py-12">
          <CheckCircle size={48} className="text-green-400 mx-auto mb-3" />
          <p className="text-white font-bold text-lg">Report Submitted</p>
          <p className="text-slate-500 text-sm mt-1">Ops team notified</p>
          {photos.length > 0 && (
            <p className="text-[11px] text-slate-600 mt-1">{photos.length} photo{photos.length > 1 ? "s" : ""} attached</p>
          )}
          <button onClick={reset} className="mt-6 text-orange-400 text-sm font-bold border border-orange-500/30 rounded-xl px-4 py-2">
            New Report
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-black text-white mb-0.5">Incident Report</h2>
            <p className="text-[11px] text-slate-500">{user.name} · {user.station}</p>
          </div>

          {/* Type */}
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Incident Type *</label>
            <div className="space-y-1.5">
              {INCIDENT_TYPES.map(t => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${type === t ? "bg-orange-500/10 border-orange-500/30 text-white font-semibold" : "bg-[#0c1929] border-slate-600/30 text-slate-400"}`}
                  data-testid={`incident-type-${t.toLowerCase().replace(/[\s/()]/g,"-")}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Severity */}
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Severity *</label>
            <div className="space-y-2">
              {SEVERITIES.map(s => (
                <button
                  key={s.value}
                  onClick={() => setSeverity(s.value)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-all ${severity === s.value ? s.color : "bg-[#0c1929] border-slate-600/30 text-slate-500"}`}
                  data-testid={`severity-${s.value}`}
                >
                  <span className="font-semibold">{s.label}</span>
                  {severity === s.value && <span className="text-xs font-bold">✓</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Description *</label>
            <textarea
              value={desc}
              onChange={e => setDesc(e.target.value)}
              placeholder="Describe what happened, where, and immediate actions taken..."
              rows={4}
              className="w-full bg-[#0c1929] border border-slate-600/40 rounded-xl px-4 py-3 text-white text-sm resize-none focus:outline-none focus:border-orange-500/50 placeholder-slate-700"
              data-testid="incident-desc"
            />
          </div>

          {/* Photo section */}
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-widest font-bold block mb-2">
              Photo Evidence {photos.length > 0 && <span className="text-orange-400 ml-1">({photos.length} attached)</span>}
            </label>

            {/* Photo previews */}
            {photos.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-3">
                {photos.map((src, idx) => (
                  <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden border border-orange-500/30" data-testid={`photo-preview-${idx}`}>
                    <img src={src} alt={`evidence ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1 right-1 w-5 h-5 bg-black/70 rounded-full flex items-center justify-center text-white hover:bg-red-500/80 transition"
                      data-testid={`remove-photo-${idx}`}
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload button */}
            <label
              className={`flex items-center justify-center gap-2 py-3.5 rounded-xl border border-dashed text-sm cursor-pointer transition-colors
                ${photoLoading ? "border-orange-500/50 text-orange-400" : "border-slate-600/30 text-slate-500 hover:border-orange-500/30 hover:text-orange-400"}`}
              data-testid="photo-upload-label"
            >
              {photoLoading ? (
                <><Loader size={15} className="animate-spin" /> Processing...</>
              ) : (
                <><Camera size={15} /><Image size={15} /> {photos.length > 0 ? "Add another photo" : "Attach photo"}</>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handleFileChange}
                className="hidden"
                data-testid="photo-file-input"
              />
            </label>
          </div>

          <button
            onClick={submit}
            disabled={saving || !type || !desc}
            className="w-full py-4 bg-red-500 hover:bg-red-400 disabled:opacity-40 text-white font-black text-base rounded-2xl transition-all active:scale-95"
            data-testid="submit-incident-btn"
          >
            {saving ? <Loader size={18} className="animate-spin mx-auto" /> : `Submit Report${photos.length > 0 ? ` with ${photos.length} Photo${photos.length > 1 ? "s" : ""}` : ""}`}
          </button>
        </div>
      )}
    </div>
  );
}
